/**
 * hz-if 条件指令处理器
 */

import { ExpressionParser, ExpressionEvaluator } from "../expression";

import type {
  IfElementState,
  GetDataFn,
  DataObject,
  DataValue,
} from "../types";

/**
 * hz-if 条件指令处理器
 */
export class IfDirective {
  /** 隐藏元素的状态（元素 -> 占位注释 & 父节点 & 作用域数据） */
  private hzIfState = new Map<Element, IfElementState>();

  private expressionParser = new ExpressionParser();
  private expressionEvaluator = new ExpressionEvaluator();

  /**
   * 处理单个元素的 hz-if 指令
   * @returns true: 元素应显示（或无 hz-if）; false: 元素已隐藏（被替换为注释）
   */
  processElement(
    element: Element,
    getData: GetDataFn,
    scopeData?: DataObject,
  ): boolean {
    let hzIfExpr = element.getAttribute("hz-if");
    if (hzIfExpr === null) {
      return true;
    }

    // 严格校验语法
    hzIfExpr = hzIfExpr.trim();
    if (hzIfExpr.startsWith("{") && hzIfExpr.endsWith("}")) {
      // 必须带 this.
      const inner = hzIfExpr.slice(1, -1).trim();
      if (!inner.startsWith("this.")) {
        console.warn(
          `hz-if syntax error: "${hzIfExpr}". When using curly braces {}, expression must start with "this.".`,
        );
        return true; // 保持显示以免破坏布局，或者 return false 隐藏，这里选择保守 warn
      }
      hzIfExpr = inner;
    } else {
      // 必须不带 this.
      if (hzIfExpr.startsWith("this.")) {
        console.warn(
          `hz-if syntax error: "${hzIfExpr}". "this." prefix is only allowed within curly braces {}.`,
        );
        return true;
      }
    }

    const shouldShow = this.evaluateExpression(hzIfExpr, getData);

    if (!shouldShow) {
      // 隐藏：若未保存过，则替换为注释并记录
      if (!this.hzIfState.has(element)) {
        const placeholder = document.createComment("hz-if placeholder");
        const parent = element.parentNode;
        if (parent) {
          parent.replaceChild(placeholder, element);
          this.hzIfState.set(element, { placeholder, parent, scopeData });
        }
      }
      return false;
    }

    return true;
  }

  /**
   * 检查并恢复应该显示的元素
   * @param getGlobalData 全局数据获取函数
   * @returns 已恢复的元素列表（需要后续处理）
   */
  restoreHiddenNodes(
    getGlobalData: (key: string) => unknown,
  ): Array<{ element: Element; scopeData?: DataObject }> {
    const restoredNodes: Array<{ element: Element; scopeData?: DataObject }> =
      [];

    for (const [element, state] of Array.from(this.hzIfState.entries())) {
      const hzIfExpr = element.getAttribute("hz-if");
      if (hzIfExpr == null) {
        continue;
      }

      // 构建合并了作用域的数据获取函数
      const { scopeData } = state;
      const getData: GetDataFn = (key: string) => {
        if (scopeData) {
          const val = this.expressionParser.getNestedValue(scopeData, key);
          if (val !== undefined) {
            return val;
          }
        }
        return getGlobalData(key) as DataValue;
      };

      const shouldShow = this.evaluateExpression(hzIfExpr, getData);
      if (shouldShow) {
        const { placeholder, parent } = state;
        if (placeholder.parentNode === parent) {
          parent.replaceChild(element, placeholder);
          this.hzIfState.delete(element);
          restoredNodes.push({ element, scopeData });
        }
      }
    }

    return restoredNodes;
  }

  /**
   * 求值 hz-if 表达式
   */
  private evaluateExpression(expr: string, getData: GetDataFn): boolean {
    try {
      const processedExpr = this.expressionParser.resolveReferences(
        expr,
        getData,
      );
      return this.expressionEvaluator.isTruthy(
        this.expressionEvaluator.evaluate(processedExpr),
      );
    } catch (error) {
      console.warn("hz-if 解析失败:", expr, error);
      return false;
    }
  }

  /**
   * 检查元素是否处于隐藏状态
   */
  isHidden(element: Element): boolean {
    return this.hzIfState.has(element);
  }

  /**
   * 清空所有状态
   */
  clear(): void {
    this.hzIfState.clear();
  }

  /**
   * @deprecated 使用 processElement 和 restoreHiddenNodes 代替
   */
  process(
    _root: Element,
    _getData: GetDataFn,
    _onRestore: (el: Element) => void,
  ): void {
    console.warn(
      "IfDirective.process is deprecated. Use processElement instead.",
    );
  }
}
